import axios, { AxiosInstance, AxiosResponse } from 'axios'
import { ApiResponse, HelloResponse, HealthCheckResponse } from '@shared/types/api'

class ApiClient {
  private client: AxiosInstance

  constructor() {
    // Use environment variable for backend URL, fallback to localhost:3001
    const baseURL = process.env.NEXT_PUBLIC_API_URL || 'http://localhost:3001/api'
    
    this.client = axios.create({
      baseURL,
      timeout: 10000,
      headers: {
        'Content-Type': 'application/json'
      }
    })

    // Request interceptor
    this.client.interceptors.request.use(
      (config) => {
        console.log(`Making ${config.method?.toUpperCase()} request to ${config.url}`)
        return config
      },
      (error) => {
        console.error('Request error:', error)
        return Promise.reject(error)
      }
    )

    // Response interceptor
    this.client.interceptors.response.use(
      (response: AxiosResponse) => {
        console.log(`Response from ${response.config.url}:`, response.status)
        return response
      },
      (error) => {
        console.error('Response error:', error.response?.data || error.message)
        return Promise.reject(error)
      }
    )
  }

  // Authentication methods
  setAuthToken(token: string | null) {
    if (token) {
      this.client.defaults.headers.common['Authorization'] = `Bearer ${token}`
    } else {
      delete this.client.defaults.headers.common['Authorization']
    }
  }

  // Generic HTTP methods
  async get<T>(url: string): Promise<AxiosResponse<T>> {
    return this.client.get<T>(url)
  }

  async post<T>(url: string, data?: any): Promise<AxiosResponse<T>> {
    return this.client.post<T>(url, data)
  }

  async put<T>(url: string, data?: any): Promise<AxiosResponse<T>> {
    return this.client.put<T>(url, data)
  }

  async delete<T>(url: string): Promise<AxiosResponse<T>> {
    return this.client.delete<T>(url)
  }

  // Legacy methods (keeping for backward compatibility)
  async getHello(): Promise<HelloResponse> {
    const response = await this.client.get<HelloResponse>('/hello')
    return response.data
  }

  async postHello(name: string): Promise<HelloResponse> {
    const response = await this.client.post<HelloResponse>('/hello', { name })
    return response.data
  }

  async getHealth(): Promise<HealthCheckResponse> {
    const response = await this.client.get<HealthCheckResponse>('/health')
    return response.data
  }

  // Consumer API methods
  async searchServicesByCategory(categoryId: string) {
    return this.client.get(`/consumer/services/search?categoryId=${categoryId}`)
  }

  async getAllServices() {
    return this.client.get('/consumer/services')
  }

  async getServiceById(serviceId: string) {
    return this.client.get(`/consumer/services/${serviceId}`)
  }

  async getConsumerProfile() {
    return this.client.get('/consumer/profile')
  }

  async createServiceRequest(data: any) {
    return this.client.post('/consumer/requests', data)
  }

  async getConsumerRequests() {
    return this.client.get('/consumer/requests')
  }

  async cancelServiceRequest(requestId: string) {
    return this.client.post(`/consumer/requests/${requestId}/cancel`)
  }

  async consumerNegotiateRequest(requestId: string, offeredPrice: number, message?: string) {
    return this.client.post(`/consumer/requests/${requestId}/negotiate`, {
      offeredPrice,
      message
    })
  }

  async consumerAcceptRequest(requestId: string) {
    return this.client.post(`/consumer/requests/${requestId}/accept`)
  }

  // Auth API methods
  async checkPhoneExists(phone: string) {
    return this.client.post('/auth/check-phone', { phone })
  }
}

export const apiClient = new ApiClient()